import { useCallback, useEffect, useRef, useState } from 'react'

interface Options {
  scrollAmount?: number
  behavior?: ScrollBehavior
  debounceDelay?: number
}

const useHorizontalScrollButtons = (options?: Options) => {
  const [showLeftButton, setShowLeftButton] = useState(false)
  const [showRightButton, setShowRightButton] = useState(false)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<number | null>(null)
  const observerRef = useRef<MutationObserver | null>(null)

  const { scrollAmount = 300, behavior = 'smooth', debounceDelay = 100 } = options || {}

  const handleScroll = useCallback(
    (direction: 'left' | 'right') => {
      const container = scrollContainerRef.current

      if (!container) return

      const amount = direction === 'left' ? -scrollAmount : scrollAmount

      container.scrollBy({ left: amount, behavior })
    },
    [scrollAmount, behavior],
  )

  const handleScrollLeft = useCallback(() => handleScroll('left'), [handleScroll])
  const handleScrollRight = useCallback(() => handleScroll('right'), [handleScroll])

  const checkScrollButtons = useCallback(() => {
    const container = scrollContainerRef.current

    if (!container) return

    const { scrollLeft, scrollWidth, clientWidth } = container

    const isAtEnd = Math.ceil(scrollLeft + clientWidth + 1) >= scrollWidth

    setShowLeftButton(scrollLeft > 0)
    setShowRightButton(scrollWidth > clientWidth && !isAtEnd)
  }, [])

  const debouncedCheckScrollButtons = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = window.setTimeout(() => {
      checkScrollButtons()
    }, debounceDelay)
  }, [checkScrollButtons, debounceDelay])

  const setupMutationObserver = useCallback(
    (container: HTMLDivElement) => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }

      const observer = new MutationObserver(() => {
        debouncedCheckScrollButtons()
      })

      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      })

      observerRef.current = observer

      return observer
    },
    [debouncedCheckScrollButtons],
  )

  useEffect(() => {
    const container = scrollContainerRef.current

    if (!container) return

    checkScrollButtons()

    const mutationObserver = setupMutationObserver(container)

    container.addEventListener('scroll', debouncedCheckScrollButtons)
    window.addEventListener('resize', debouncedCheckScrollButtons)

    return () => {
      mutationObserver.disconnect()

      container.removeEventListener('scroll', debouncedCheckScrollButtons)
      window.removeEventListener('resize', debouncedCheckScrollButtons)

      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [checkScrollButtons, debouncedCheckScrollButtons, setupMutationObserver])

  return {
    scrollContainerRef,
    showLeftButton,
    showRightButton,
    handleScrollLeft,
    handleScrollRight,
  }
}

export default useHorizontalScrollButtons
